import { indicationOptions } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { Box, Typography } from "@material-ui/core";
import type { BladderWorksheetData } from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { EPOCUS_TEXT_SECONDARY, rem } from "../epocus-palette";
import {
  AllNormalToggle,
  BooleanToggle,
  EnumChipSelect,
  IndicationChips,
  NotesField,
  NumericInput,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "./worksheet-primitives";

const INDICATION_OPTIONS = indicationOptions("bladder");

const VIEW_OPTIONS = [
  { key: "sagittal", label: "Sagittal" },
  { key: "transverse", label: "Transverse" },
] as const;

const VOLUME_METHOD_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "manual", label: "Manual" },
] as const;

const DISTENSION_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "mild", label: "Mild" },
  { value: "moderate", label: "Moderate" },
  { value: "severe", label: "Severe" },
] as const;

export const isBladderComplete = (data: BladderWorksheetData): boolean =>
  data.findings.distension !== null && data.findings.wallAbnormalities !== null;

const FindingLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

export const BladderWorksheet: FC<{
  data: BladderWorksheetData;
  onChange: (data: BladderWorksheetData) => void;
  isAllNormal: boolean;
  onToggleAllNormal: () => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
}> = ({
  data,
  onChange,
  isAllNormal,
  onToggleAllNormal,
  readOnly,
  imageGallery,
}) => {
  const updateFindings = (patch: Partial<BladderWorksheetData["findings"]>) => {
    onChange({ ...data, findings: { ...data.findings, ...patch } });
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <AllNormalToggle
        isAllNormal={isAllNormal}
        onToggle={onToggleAllNormal}
        disabled={readOnly}
      />

      <WorksheetColumns>
        <WorksheetColumnLeft>
          <WorksheetSection label="Indication">
            <IndicationChips
              options={[...INDICATION_OPTIONS]}
              selected={data.indication}
              onChange={(v) =>
                onChange({
                  ...data,
                  indication: v as BladderWorksheetData["indication"],
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Views">
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
            >
              Tap to toggle obtained status
            </Typography>
            <ViewChecklist
              views={[...VIEW_OPTIONS]}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({
                  ...data,
                  views: { ...data.views, [key]: checked },
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnLeft>

        <WorksheetColumnRight>
          <WorksheetSection label="Bladder Volume">
            <FindingLabel>Method</FindingLabel>
            <EnumChipSelect
              options={[...VOLUME_METHOD_OPTIONS]}
              selected={data.findings.bladderVolumeMethod}
              onChange={(v) =>
                updateFindings({
                  bladderVolumeMethod:
                    v as BladderWorksheetData["findings"]["bladderVolumeMethod"],
                })
              }
              disabled={readOnly}
            />
            <NumericInput
              value={data.findings.bladderVolumeMl}
              onChange={(v) => updateFindings({ bladderVolumeMl: v })}
              unit="mL"
              step={1}
              label="Pre-void Volume"
              disabled={readOnly}
            />
            <NumericInput
              value={data.findings.postVoidVolumeMl}
              onChange={(v) => updateFindings({ postVoidVolumeMl: v })}
              unit="mL"
              step={1}
              label="Post-void Volume"
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Findings">
            <FindingLabel>Distension</FindingLabel>
            <EnumChipSelect
              options={[...DISTENSION_OPTIONS]}
              selected={data.findings.distension}
              onChange={(v) =>
                updateFindings({
                  distension:
                    v as BladderWorksheetData["findings"]["distension"],
                })
              }
              disabled={readOnly}
            />

            <FindingLabel>Wall Abnormalities</FindingLabel>
            <BooleanToggle
              value={data.findings.wallAbnormalities}
              onChange={(v) => updateFindings({ wallAbnormalities: v })}
              disabled={readOnly}
            />
          </WorksheetSection>

          {imageGallery && (
            <WorksheetSection label="Images / Clips">
              {imageGallery}
            </WorksheetSection>
          )}

          <WorksheetSection label="Notes">
            <NotesField
              value={data.notes}
              onChange={(v) => onChange({ ...data, notes: v })}
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnRight>
      </WorksheetColumns>
    </Box>
  );
};
